<script setup lang="ts">
  import { computed, provide } from 'vue';
  import { VueFinalModal } from 'vue-final-modal';

  import type { DocumentRevision } from '@/entities/Document';
  import type { OrderOptions } from '@/shared/lib';
  import { DEFAULT_PAGE_SIZE } from '@/shared/config';
  import { SortOrderInjectionKey } from '@/shared/lib/sortOrder';
  import { ColoredButton, FieldSearchBar, UiModalHeader } from '@/shared/ui';

  import type { DocumentSelectionMode } from '../../model/useDocumentSelectionModal';
  import { useDocumentSelectionModal } from '../../model/useDocumentSelectionModal';
  import {
    createDocumentSelectionFilterContext,
    DocumentSelectionFilterInjectionKey,
  } from '../../model/documentSelectionFilterContext';
  import DocumentSelectionTable from './DocumentSelectionTable.vue';

  const props = withDefaults(
    defineProps<{
      title?: string;
      tableTitle?: string;
      selectionMode?: DocumentSelectionMode;
      maxSelections?: number;
      amountOptions?: Array<{ id: number; name: string }>;
      defaultAmount?: number;
      showSort?: boolean;
      showAdvancedFilter?: boolean;
      showSelectAll?: boolean;
      showViewAction?: boolean;
      alreadySelectedRevisionIds?: number[];
      initialSelectedRevisionId?: number | null;
      initialSelectedRevisionIds?: number[];
      initialSelectedRevisions?: DocumentRevision[];
      confirmButtonText?: string;
      selectionLimitMessage?: string;
      searchUrl?: string;
      orderOptions?: OrderOptions[];
      fixedFilters?: Record<string, unknown>;
      isSubmitting?: boolean;
      onDemandRevisions?: boolean;
      requireExplicitClose?: boolean;
    }>(),
    {
      title: 'Selecionar Documento',
      tableTitle: 'Selecione o Documento',
      selectionMode: 'single',
      maxSelections: undefined,
      amountOptions: undefined,
      defaultAmount: DEFAULT_PAGE_SIZE,
      showSort: false,
      showAdvancedFilter: false,
      showSelectAll: false,
      showViewAction: false,
      alreadySelectedRevisionIds: () => [],
      initialSelectedRevisionId: null,
      initialSelectedRevisionIds: () => [],
      initialSelectedRevisions: () => [],
      confirmButtonText: 'Adicionar',
      selectionLimitMessage: undefined,
      searchUrl: undefined,
      orderOptions: undefined,
      fixedFilters: () => ({}),
      isSubmitting: false,
      onDemandRevisions: false,
      requireExplicitClose: false,
    }
  );

  const emit = defineEmits<{
    (e: 'close'): void;
    (e: 'select', value: DocumentRevision): void;
    (e: 'select-multiple', value: DocumentRevision[]): void;
    (e: 'view', value: DocumentRevision): void;
  }>();

  const {
    allVisibleRevisionsSelected,
    amountOptions,
    canAddMore,
    canSelectRevision,
    currentOrder,
    currentPage,
    currentSort,
    documentRows,
    formatRevisionLabel,
    handleDocumentFilterChange,
    handleFilterApply,
    handlePageChanged,
    handleRevisionSelected,
    handleSearch,
    handleSort,
    hasMoreRevisions,
    isInfiniteScrollMode,
    isDocumentExpanded,
    isRevisionActive,
    isRevisionAlreadyAdded,
    isRevisionSelected,
    getPreviousRevisionsState,
    retryLoadPreviousRevisions,
    loading,
    loadingMore,
    metadata,
    orderOptions,
    searchFieldId,
    searchFields,
    searchTerm,
    selectedAmount,
    selectedRevision,
    selectedRevisionCount,
    selectedRevisions,
    selectionLimitError,
    toggleAllVisibleRevisions,
    toggleDocumentExpansion,
    toggleRevision,
  } = useDocumentSelectionModal({
    selectionMode: props.selectionMode,
    maxSelections: props.maxSelections,
    amountOptions: props.amountOptions,
    defaultAmount: props.defaultAmount,
    enableSort: props.showSort,
    enableAdvancedFilter: props.showAdvancedFilter,
    alreadySelectedRevisionIds: props.alreadySelectedRevisionIds,
    initialSelectedRevisionId: props.initialSelectedRevisionId,
    initialSelectedRevisionIds: props.initialSelectedRevisionIds,
    initialSelectedRevisions: props.initialSelectedRevisions,
    selectionLimitMessage: props.selectionLimitMessage,
    searchUrl: props.searchUrl,
    orderOptions: props.orderOptions,
    fixedFilters: props.fixedFilters,
    onDemandRevisions: props.onDemandRevisions,
  });

  provide(SortOrderInjectionKey, { sort: currentSort, order: currentOrder });

  const filterContext = createDocumentSelectionFilterContext((filters) =>
    handleFilterApply(filters as Record<string, unknown>)
  );
  provide(DocumentSelectionFilterInjectionKey, filterContext);

  const canConfirmSelection = computed(() =>
    props.selectionMode === 'multiple'
      ? selectedRevisionCount.value > 0
      : selectedRevision.value !== null
  );

  const canDismiss = computed(() => !props.isSubmitting && !props.requireExplicitClose);

  /**
   * Close requested by the user: the "Cancelar" button or the header's "X". The backdrop and Esc
   * never reach here, because `clickToClose`/`escToClose` are turned off while submitting or while
   * `requireExplicitClose` is set: the library hides the modal on its own before emitting, so
   * ignoring the emit would leave it hidden and still mounted, with no surface for the duplicate
   * toast or the preserved selection.
   */
  function handleClose(): void {
    if (props.isSubmitting) {
      return;
    }

    emit('close');
  }

  function handleConfirm(): void {
    if (!canConfirmSelection.value || props.isSubmitting) {
      return;
    }

    if (props.selectionMode === 'multiple') {
      emit('select-multiple', selectedRevisions.value);
      return;
    }

    if (!selectedRevision.value) {
      return;
    }

    emit('select', selectedRevision.value);
  }
</script>

<template>
  <VueFinalModal
    :model-value="true"
    :click-to-close="canDismiss"
    :esc-to-close="canDismiss"
    class="flex items-center justify-center"
    content-class="w-[95vw] max-w-[1260px] overflow-hidden rounded-2xl bg-white-01 shadow-lg"
    overlay-transition="vfm-fade"
    content-transition="vfm-fade"
    @update:model-value="
      (value: boolean) => {
        if (!value) handleClose();
      }
    "
  >
    <div class="flex max-h-[90vh] flex-col">
      <UiModalHeader
        class="border-b border-gray-02 px-6 py-4 text-sm"
        :title="title"
        @close="handleClose()"
      />

      <div class="flex-1 overflow-y-auto px-6 py-6">
        <FieldSearchBar
          v-model:search-field-id="searchFieldId"
          :search-fields="searchFields"
          :initial-search-term="searchTerm"
          :independent-search-field="true"
          @search="handleSearch"
        />

        <DocumentSelectionTable
          :amount-options="amountOptions"
          :all-visible-revisions-selected="allVisibleRevisionsSelected"
          :can-add-more="canAddMore"
          :can-select-revision="canSelectRevision"
          :current-page="currentPage"
          :document-rows="documentRows"
          :format-revision-label="formatRevisionLabel"
          :has-more-revisions="hasMoreRevisions"
          :is-infinite-scroll-mode="isInfiniteScrollMode"
          :is-document-expanded="isDocumentExpanded"
          :is-filter-active="filterContext.isFilterActive.value"
          :is-revision-active="isRevisionActive"
          :is-revision-already-added="isRevisionAlreadyAdded"
          :is-revision-selected="isRevisionSelected"
          :on-demand-revisions="onDemandRevisions"
          :get-previous-revisions-state="getPreviousRevisionsState"
          :loading="loading"
          :loading-more="loadingMore"
          :metadata="metadata"
          :order-options="orderOptions"
          :selected-amount="selectedAmount"
          :selection-mode="selectionMode"
          :show-advanced-filter="showAdvancedFilter"
          :show-select-all="showSelectAll"
          :show-sort="showSort"
          :show-view-action="showViewAction"
          :table-title="tableTitle"
          @row-click="handleRevisionSelected($event.id)"
          @toggle="toggleRevision"
          @toggle-all="toggleAllVisibleRevisions"
          @expand="toggleDocumentExpansion"
          @update:sort="handleSort"
          @update:amount="handleDocumentFilterChange"
          @page="handlePageChanged"
          @view="emit('view', $event)"
          @retry-previous-revisions="retryLoadPreviousRevisions"
        />
      </div>

      <div class="flex items-center justify-between px-6 py-4">
        <div class="flex flex-col gap-1">
          <span v-if="selectionMode === 'multiple'" class="text-sm text-gray-03">
            {{ selectedRevisionCount }} selecionado(s)
          </span>
          <p
            v-if="selectionLimitError"
            class="text-xs text-red-16"
            data-testid="selection-limit-error"
          >
            {{ selectionLimitError }}
          </p>
        </div>
        <div class="flex gap-6">
          <ColoredButton
            text="Cancelar"
            outlined
            color="gray-03"
            border-color="gray-03"
            :disabled="isSubmitting"
            @click="handleClose()"
          />
          <ColoredButton
            :text="confirmButtonText"
            :disabled="!canConfirmSelection || isSubmitting"
            @click="handleConfirm"
          />
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>
